import { cloneDeep } from '@graphcommerce/graphql'
import { useProductListParamsContext } from '@graphcommerce/magento-product'
import { FilterRangeType } from '@graphcommerce/magento-product/components/ProductListFilters/FilterRangeType'
import { ProductListFiltersFragment } from '@graphcommerce/magento-product/components/ProductListFilters/ProductListFilters.gql'
import { FilterTypes } from '@graphcommerce/magento-product/components/ProductListItems/filterTypes'
import { ChipMenuProps } from '@graphcommerce/next-ui'
import { i18n } from '@lingui/core'
import { Trans } from '@lingui/react'
import { Paper } from '@mui/material'
import { useCallback, useState } from 'react'
import { useProductListLinkReplace } from '../../../hooks/Product/useProductListLinkReplace'
import useWindowSize from '../../../hooks/useWindowSize'
import { Icon } from '../../Layout/Icon'
import { FilterCheckboxType } from './FilterCheckboxType'
import { FilterEqualType } from './FilterEqualType'
import { FilterRangeAsOptionsType } from './FilterRangeAsOptionsType'

export type ProductFiltersProps = ProductListFiltersFragment & {
  filterTypes: FilterTypes
} & Omit<ChipMenuProps, 'selected' | 'selectedLabel' | 'children' | 'label' | 'onDelete'> & {
    hideCategoryFilter?: boolean
  }

export function ProductListFilters(props: ProductFiltersProps) {
  const { aggregations, filterTypes, hideCategoryFilter, ...chipMenuProps } = props
  const { isMobile } = useWindowSize()
  const [isOpen, setIsOpen] = useState(false)
  const { params } = useProductListParamsContext()
  const replaceRoute = useProductListLinkReplace({ scroll: false })

  const clearFilters = useCallback(() => {
    const linkParams = cloneDeep(params)
    linkParams.filters = {
      category_uid: linkParams.filters.category_uid,
    }
    delete linkParams.currentPage

    replaceRoute(linkParams)
  }, [replaceRoute, params])

  const aggregationsDisplay = hideCategoryFilter
    ? aggregations?.filter((item) => item?.attribute_code !== 'category_uid')
    : aggregations

  if (isMobile && !isOpen) {
    return (
      <div className='my-[20px]'>
        <button
          onClick={() => setIsOpen(true)}
          type='button'
          className='mb-[20px] ml-5 mt-[10px] inline-flex items-center justify-center lg:mb-0 lg:mt-0'
        >
          <span className='Type-XXL-Bold  mr-[8px] inline-flex h-[20px] w-[20px] items-center justify-center'>
            +
          </span>
          <span className='Type-XL-Bold'>
            <Trans id='Product Filters' />
          </span>
        </button>
      </div>
    )
  }

  return (
    <Paper
      elevation={3}
      className='relative grid w-full grid-flow-row gap-[20px] rounded-[5px] bg-pure-white pb-[40px] md:mb-[200px] md:min-w-[300px] md:pb-[40px] lg:rounded-none lg:border-0'
    >
      <h3 className='Type-XXL-Bold hidden w-full border-b-2 border-light-gray py-[10px] pl-[10px] text-tight-black md:pl-[20px] lg:inline '>
        <Trans id='Product Filters' />
      </h3>
      <div className='m-[10px] md:m-[20px] lg:mt-0'>
        <div className='flex items-center justify-between'>
          <div className='hidden lg:inline'>
            {Object.keys(params.filters).length > 1 && (
              <button
                className='Type-XL-Bold mb-[5px] mt-[10px] flex items-center text-left text-material-ui-blue hover:text-material-ui-dark-blue'
                type='button'
                onClick={() => clearFilters()}
              >
                <Trans id='Clear all filters' />
              </button>
            )}
          </div>
        </div>
        <div className='flex items-center justify-between'>
          <div className='lg:hidden'>
            {Object.keys(params.filters).length > 1 && (
              <button
                className='Type-XL-Bold text-left text-material-ui-blue hover:text-material-ui-dark-blue'
                type='button'
                onClick={() => clearFilters()}
              >
                <Trans id='Clear Filters' />
              </button>
            )}
          </div>
          {isMobile && (
            <button type='button' onClick={() => setIsOpen(false)}>
              <Icon name='disabled_by_default' className='text-4xl text-70-grey' />
            </button>
          )}
        </div>
        {aggregationsDisplay?.map((aggregation) => {
          if (
            !aggregation?.attribute_code ||
            ['category_id', 'price'].includes(aggregation?.attribute_code)
          )
            return null

          switch (filterTypes[aggregation.attribute_code]) {
            case 'FilterEqualTypeInput':
              if (
                aggregation.options?.[0]?.label === '0' ||
                aggregation.options?.[1]?.label === '0' ||
                aggregation.options?.[0]?.label === '1' ||
                aggregation.options?.[1]?.label === '1'
              ) {
                return (
                  <FilterCheckboxType
                    key={aggregation.attribute_code}
                    {...aggregation}
                    {...chipMenuProps}
                  />
                )
              }

              return (
                <FilterEqualType
                  key={aggregation.attribute_code}
                  {...aggregation}
                  {...chipMenuProps}
                />
              )

            case 'FilterRangeTypeInput':
            case undefined:
              if (aggregation?.attribute_code === 'production_time') {
                return (
                  <FilterRangeAsOptionsType
                    key='production_time_bucket'
                    {...aggregation}
                    attribute_code='production_time'
                    label='Production Time'
                    options={[
                      {
                        value: '0-5',
                        label: i18n._(/* i18n */ 'Less than one week'),
                        __typename: 'AggregationOption',
                      },
                      {
                        value: '0-10',
                        label: i18n._(/* i18n */ 'Less than two weeks'),
                        __typename: 'AggregationOption',
                      },
                      {
                        value: '0-15',
                        label: i18n._(/* i18n */ 'Less than three weeks'),
                        __typename: 'AggregationOption',
                      },
                      {
                        value: '0-20',
                        label: i18n._(/* i18n */ 'Less than four weeks'),
                        __typename: 'AggregationOption',
                      },
                    ]}
                    {...chipMenuProps}
                  />
                )
              }
              return (
                <FilterRangeType
                  key={aggregation.attribute_code}
                  {...aggregation}
                  {...chipMenuProps}
                />
              )
          }
          return null
        })}
      </div>
    </Paper>
  )
}
